import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

const formSchema = z.object({
  name: z.string().trim().min(2, "Numele trebuie să aibă cel puțin 2 caractere").max(100, "Numele este prea lung"),
  email: z.string().trim().email("Adresa de email nu este validă").max(255, "Email-ul este prea lung"),
  phone: z.string().trim().max(20, "Numărul de telefon este prea lung").optional(),
  subject: z.string().trim().min(3, "Subiectul trebuie să aibă cel puțin 3 caractere").max(200, "Subiectul este prea lung"),
  message: z.string().trim().min(10, "Mesajul trebuie să aibă cel puțin 10 caractere").max(2000, "Mesajul este prea lung"),
});

type FormData = z.infer<typeof formSchema>;

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      const { data: result, error } = await supabase.functions.invoke('send-contact-email', {
        body: data,
      });

      if (error) {
        throw error;
      }

      if (result?.success) {
        toast({
          title: "Succes!",
          description: result.message || "Mesajul a fost trimis cu succes!",
        });
        form.reset();
      } else {
        throw new Error(result?.error || "A apărut o eroare");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast({
        title: "Eroare",
        description: error instanceof Error ? error.message : "Nu s-a putut trimite mesajul. Vă rugăm să încercați din nou.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-cream/30 to-background relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }} />
      
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <Mail className="w-4 h-4 text-primary" />
            <span className="text-primary font-semibold text-sm font-lora">Ia legătura cu noi</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold text-primary mb-6 font-playfair">
            Contactează-ne
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto font-lora leading-relaxed">
            Suntem aici pentru a răspunde întrebărilor tale și pentru a te ajuta să găsești sau să creezi cadoul perfect
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-16 max-w-6xl mx-auto">
          <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <h3 className="text-3xl font-bold text-foreground mb-8 font-playfair">
              Trimite-ne un mesaj
            </h3>
            <p className="text-muted-foreground font-lora mb-8 text-lg">
              Așteptăm cu interes mesajul dvs. și vă vom răspunde în cel mai scurt timp.
            </p>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input 
                          placeholder="Nume *" 
                          className="bg-card/50 backdrop-blur-sm border-2 border-border/50 focus:border-primary/50 h-14 text-lg rounded-xl transition-all duration-300"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-sm" />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input 
                          type="email"
                          placeholder="Email *" 
                          className="bg-card/50 backdrop-blur-sm border-2 border-border/50 focus:border-primary/50 h-14 text-lg rounded-xl transition-all duration-300"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-sm" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input 
                          type="tel"
                          placeholder="Telefon (opțional)" 
                          className="bg-card/50 backdrop-blur-sm border-2 border-border/50 focus:border-primary/50 h-14 text-lg rounded-xl transition-all duration-300"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-sm" />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input 
                          placeholder="Subiect *" 
                          className="bg-card/50 backdrop-blur-sm border-2 border-border/50 focus:border-primary/50 h-14 text-lg rounded-xl transition-all duration-300"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-sm" />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder="Mesajul tău *"
                          rows={6}
                          className="bg-card/50 backdrop-blur-sm border-2 border-border/50 focus:border-primary/50 text-lg rounded-xl transition-all duration-300 font-lora resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-sm" />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit"
                  size="lg" 
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-7 rounded-full shadow-xl hover:shadow-2xl transition-all duration-500 hover-scale hover-glow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Se trimite...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Trimite mesaj
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </div>

          <div className="animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <h3 className="text-3xl font-bold text-foreground mb-8 font-playfair">
              Informații de contact
            </h3>
            <div className="space-y-8">
              <div className="flex items-start gap-5 p-6 bg-card/50 backdrop-blur-sm rounded-2xl border-2 border-border/50 hover:border-primary/30 transition-all duration-300 hover-scale">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-foreground mb-2 text-lg font-playfair">Telefon</h4>
                  <a href="tel:+40722637470" className="text-muted-foreground hover:text-primary transition-colors text-lg font-lora">
                    +40 722 637 470
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-5 p-6 bg-card/50 backdrop-blur-sm rounded-2xl border-2 border-border/50 hover:border-primary/30 transition-all duration-300 hover-scale">
                <div className="w-14 h-14 bg-gold/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-7 h-7 text-gold" />
                </div>
                <div>
                  <h4 className="font-bold text-foreground mb-2 text-lg font-playfair">Email</h4>
                  <a href="mailto:idaco_traditional@yahoo.com" className="text-muted-foreground hover:text-primary transition-colors text-lg font-lora break-all">
                    idaco_traditional@yahoo.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-5 p-6 bg-card/50 backdrop-blur-sm rounded-2xl border-2 border-border/50 hover:border-primary/30 transition-all duration-300 hover-scale">
                <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-7 h-7 text-accent" />
                </div>
                <div>
                  <h4 className="font-bold text-foreground mb-2 text-lg font-playfair">Adresă</h4>
                  <p className="text-muted-foreground text-lg font-lora">
                    Intrarea Tufanului 7<br />
                    Otopeni 075100
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-8 bg-gradient-to-br from-primary/10 to-gold/10 rounded-2xl border-2 border-primary/20 backdrop-blur-sm">
              <h4 className="font-bold text-foreground mb-4 text-xl font-playfair">Program</h4>
              <div className="space-y-2 font-lora text-lg">
                <p className="text-muted-foreground">Luni - Vineri: <span className="text-foreground font-semibold">09:00 - 15:00</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
